import type { EpisodeManifest } from "@scrile/contracts";

/**
 * Расшифровка серии: какую строку подсвечивать в момент t.
 */

export type TranscriptLine = EpisodeManifest["transcript"][number];

/**
 * Сколько секунд держать подсветку на строке после её конца. Между репликами почти
 * всегда пауза; если гасить подсветку сразу, она мигает на каждой паузе. Но после
 * долгой тишины (звуки кухни, музыка) держать старую строку — врать, где мы.
 */
export const HOLD_SECONDS = 2;

/** Порядок по началу и конец не раньше начала: сервер может прислать что угодно. */
export function normalizeTranscript(lines: readonly TranscriptLine[]): TranscriptLine[] {
  return lines
    .map((line) => ({ ...line, endSec: Math.max(line.startSec, line.endSec) }))
    .sort((a, b) => a.startSec - b.startSec);
}

/**
 * Индекс строки для подсветки в момент t или -1.
 *
 * Правило: последняя начавшаяся строка, пока она звучит или не прошло HOLD_SECONDS
 * после её конца (и следующая ещё не началась). Строки отсортированы по startSec
 * (normalizeTranscript); поиск двоичный — плеер спрашивает четыре раза в секунду.
 */
export function currentLineIndex(
  lines: readonly TranscriptLine[],
  t: number,
  holdSeconds: number = HOLD_SECONDS,
): number {
  if (lines.length === 0 || !Number.isFinite(t)) return -1;
  let low = 0;
  let high = lines.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const line = lines[mid];
    if (line && line.startSec <= t) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  if (found === -1) return -1;
  const line = lines[found];
  if (!line) return -1;
  return t < line.endSec + holdSeconds ? found : -1;
}
